import jwt_decode from 'jwt-decode';
import includes = require('lodash/includes');
import {IFacebookResponse} from '../facebook-auth/facebook-auth';
import {IGoogleResponse} from '../google-auth/google-auth';
import {IUserRequest, TProvider} from '../../common/types';
import * as API_Error from '../../utils/API-errors';

export const KEY_ENTER: number = 13;
export const KEY_ESC: number = 27;
export const COUNT_FAILED_SUBMIT_TO_SHOW_CAPTCHA: number = 10;
export const FACEBOOK: string = 'facebook';
export const GOOGLE: string = 'google';

export type FieldName = 'email' | 'password' | 'firstName' | 'lastName' | 'connectionRefused';

export function getFirstAllowedErrorMessage(
  errors: Array<string>,
  allowedErrors: Array<string> = [
    API_Error.USER_NOT_FOUND,
    API_Error.USER_INFORMATION_IS_MISSING,
    API_Error.WRONG_PASSWORD,
    API_Error.PASSWORD_MISMATCH,
    API_Error.MISSING_FIRST_NAME,
    API_Error.MISSING_LAST_NAME,
    API_Error.MISSING_EMAIL,
    API_Error.MISSING_PASSWORD,
    API_Error.INVALID_EMAIL,
    API_Error.INVALID_PROVIDER_TOKEN,
    API_Error.EMAIL_ALREADY_USED,
    API_Error.CONNECTION_REFUSED,
  ],
  overrides: { [name: string]: string } = {},
): string {
  // noinspection TsLint
  for (let allowedError of allowedErrors) {
    if (includes(errors, allowedError)) {
      return overrides[allowedError] || API_Error.errorToMessage(allowedError);
    }
  }
  return '';
}

export function getErrorMessage(field: FieldName, provider: TProvider, errors: Array<string>): string {
  if (provider !== 'pramp') {
    return '';
  }

  switch (field) {
    case 'email':
      return getFirstAllowedErrorMessage(errors, [
        API_Error.MISSING_EMAIL,
        API_Error.INVALID_EMAIL,
        API_Error.EMAIL_ALREADY_USED,
        API_Error.USER_NOT_FOUND,
        API_Error.COMPANY_NOT_FOUND,
      ]);
    case 'password':
      return getFirstAllowedErrorMessage(errors,
        [API_Error.MISSING_PASSWORD, API_Error.WRONG_PASSWORD, API_Error.PASSWORD_MISMATCH]);
    case 'firstName':
      return getFirstAllowedErrorMessage(errors, [API_Error.MISSING_FIRST_NAME]);
    case 'lastName':
      return getFirstAllowedErrorMessage(errors, [API_Error.MISSING_LAST_NAME]);
    case API_Error.CONNECTION_REFUSED:
      return getFirstAllowedErrorMessage(errors, [API_Error.CONNECTION_REFUSED]);
    default:
      return '';
  }
}

export function mapGoogleResponse(response: IGoogleResponse): IUserRequest {
  const {
    email,
    given_name: firstName,
    family_name: lastName,
    sub: googleAccountId,
  } = jwt_decode(response.credential);

  return {
    firstName,
    lastName,
    email,
    provider: {
      name: 'google',
      id: googleAccountId,
      profile: {},
    },
  };
}

export function mapFacebookResponse(response: IFacebookResponse): IUserRequest {
  return {
    firstName: response.first_name,
    lastName: response.last_name,
    email: response.email,
    provider: {
      name: 'facebook',
      id: response.id,
      token: response.accessToken,
      profile: response,
    },
  };
}

export const getConnectionRefusedError = (provider: TProvider, errors: Array<string>): string => (
  getErrorMessage(API_Error.CONNECTION_REFUSED, provider, errors)
);

export const getSignInApiError = (provider: TProvider, errors: Array<string>): string => (
  getErrorMessage('email', provider, errors)
  || getErrorMessage('password', provider, errors)
);
